import ThreeDotMenu from "@/Shared/Components/ThreeDotMenu";
import { GlobeIcon } from "@phosphor-icons/react/Globe";
import { Button } from "@freightsimple/ui-core/Controls/Button";

import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import { PreferredBrokerType } from "@freightsimple/generated-dashboard-openapi-client/models/PreferredBrokerType";
import { formatPhone } from "@/Helpers/formatPhone";
import { fullStateOrProvinceName } from "@/Helpers/fullStateOrProvinceName";
import BrokersPreferredBrokerPill from "@/Shared/Brokers/CustomComponents/BrokersPreferredBrokerPill";

// Replicas of the legacy styled-components / Cargo Box (Medium shadow).
const boxClasses =
  "relative w-full min-w-0 max-w-[420px] rounded-[3px] border border-border-strong p-8 [box-shadow:var(--color-shadow-ambient)_0px_3px_12px]";
const sectionLabelClasses = "h-4 text-xs font-bold text-foreground";
const sectionLineClasses =
  "w-full break-words text-xs font-light text-content-muted";

interface BrokerBoxProps {
  broker: Broker;
  onModify: () => void;
  showMenu: boolean;
  onDelete?: (() => void) | undefined;
  preferredNorthbound: boolean;
  preferredSouthbound: boolean;
  onSelectPreferred?:
    | ((preferredType: PreferredBrokerType) => void)
    | undefined;
}
function BrokersBox(props: BrokerBoxProps) {
  const { businessName, address, contact } = props.broker;
  const { preferredNorthbound, preferredSouthbound } = props;

  function generateSelectedIndex() {
    if (preferredNorthbound && preferredSouthbound) {
      return 0;
    }

    if (preferredNorthbound) {
      return 1;
    }

    if (preferredSouthbound) {
      return 2;
    }

    return 3;
  }

  function onSelectIndex(index: number) {
    if (props.onSelectPreferred === undefined) {
      return;
    }

    if (index === 0) {
      return props.onSelectPreferred(PreferredBrokerType.All);
    }

    if (index === 1) {
      return props.onSelectPreferred(PreferredBrokerType.Northbound);
    }

    if (index === 2) {
      return props.onSelectPreferred(PreferredBrokerType.Southbound);
    }

    if (index === 3) {
      return props.onSelectPreferred(PreferredBrokerType.None);
    }
  }

  return (
    <div className={boxClasses}>
      <div
        className="flex w-full flex-col items-start"
        data-testid="broker-box"
      >
        <div className="flex w-full flex-row items-center justify-between gap-4">
          <div className="flex min-w-0 flex-row items-center">
            <GlobeIcon
              className="relative mr-2 h-5 w-5 shrink-0 text-brand-text"
              weight="fill"
            />
            <div className="truncate text-[18px] font-medium text-foreground">
              {businessName}
            </div>
          </div>

          {props.showMenu && (
            <ThreeDotMenu
              items={[
                {
                  name: "Modify",
                  action: function () {
                    props.onModify();
                  },
                },
                {
                  name: "Divider",
                  action: function () {},
                },
                {
                  name: "Mark Preferred For",
                  subitems: [
                    "All Shipments",
                    "United States Pickups",
                    "Canada Pickups",
                    "None",
                  ],
                  selectedIndex: generateSelectedIndex(),
                  action: function (selectedIndex: number) {
                    onSelectIndex(selectedIndex);
                  },
                },
                {
                  name: "Divider",
                  action: function () {},
                },
                {
                  name: "Delete",
                  tone: "danger",
                  action: function () {
                    if (props.onDelete !== undefined) {
                      props.onDelete();
                    }
                  },
                },
              ]}
            />
          )}
          {!props.showMenu && (
            <Button size="link" onClick={props.onModify}>
              Modify
            </Button>
          )}
        </div>
        <div className="h-8" />
        <BrokersPreferredBrokerPill
          preferredNorthbound={preferredNorthbound}
          preferredSouthbound={preferredSouthbound}
        />
        <div className="h-4" />
        <div className="flex w-full flex-row items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-col items-start">
            <div className={sectionLabelClasses}>Address</div>
            <div className={sectionLineClasses}>{address.addressLine}</div>
            {address.addressLine2 && (
              <div className={sectionLineClasses}>{address.addressLine2}</div>
            )}
            <div className={sectionLineClasses}>{address.city}</div>
            <div className={sectionLineClasses}>
              {fullStateOrProvinceName(address.stateOrProvinceCode)}
            </div>
            <div className={sectionLineClasses}>{address.postalCode}</div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col items-start">
            <div className={sectionLabelClasses}>Contact</div>
            <div className={sectionLineClasses}>{contact.contactName}</div>
            <div className={`${sectionLineClasses} truncate`}>
              {contact.emailAddress}
            </div>
            <div className={sectionLineClasses}>
              {formatPhone(contact.phoneNumber, contact.phoneNumberExtension)}
            </div>
          </div>
        </div>

        {props.broker.accountNumber && (
          <>
            <div className="h-4" />
            <div className="flex w-full min-w-0 flex-col items-start">
              <div className={sectionLabelClasses}>Account Number</div>
              <div className={sectionLineClasses}>
                {props.broker.accountNumber}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
export default BrokersBox;
